import { DeleteOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Row, Space, Tooltip, Typography, Upload } from 'antd';
import { UserGenderEnum, useUpdateAvatarMeUserMutation } from 'api/UsersApi';
import EditAccountModalComponent, {
  EDIT_PROFILE_MODAL_NAME
} from 'components/Account/EditAccountModalComponent/EditAccountModalComponent';
import HipaaStatusTag from 'components/Account/HipaaStatusTag/HipaaStatusTag';
import UpdatePartnerModalComponent, {
  UPDATE_PARTNER_MODAL_NAME
} from 'components/Account/UpdatePartnerModalComponent/UpdatePartnerModalComponent';
import { HIPAA_REVOKE_MODAL_NAME } from 'components/Modals/HipaaRevokeModal/HipaaRevokeModal';
import UserAvatar from 'components/UiKit/UserAvatar/UserAvatar';
import { useSelector } from 'react-redux';
import { selectAuthUser } from 'store/authSlice';
import { openModal } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { getFullName, logging } from 'utils/utils';
import './AccountSettingsComponent.css';

function AccountSettingsComponent() {
  const dispatch = useAppDispatch();
  const user = useSelector(selectAuthUser);

  const [updateAvatarMeUser] = useUpdateAvatarMeUserMutation();

  const getUserGender = (gender: UserGenderEnum) => {
    if (gender === UserGenderEnum.Male) {
      return 'Male';
    } else if (gender === UserGenderEnum.Female) {
      return 'Female';
    } else if (gender === UserGenderEnum.NotShared) {
      return 'Prefer not to share';
    } else {
      return 'Not set';
    }
  };

  const updateAvatar = async (file?: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file || '');

      await updateAvatarMeUser({ formData }).unwrap();
    } catch (e) {
      logging(e);
    }
  };

  const handleEditProfile = () => {
    dispatch(openModal({ name: EDIT_PROFILE_MODAL_NAME }));
  };

  const handleUpdatePartner = () => {
    dispatch(openModal({ name: UPDATE_PARTNER_MODAL_NAME }));
  };

  const handleManageHipaa = () => {
    dispatch(openModal({ name: HIPAA_REVOKE_MODAL_NAME }));
  };

  const renderInfoField = (label: string, value: string) => {
    return (
      <div className="info-field">
        <div className="label">{label}</div>
        <Typography.Text>{value}</Typography.Text>
      </div>
    );
  };

  const affiliateLink = `${window.location.protocol}//${window.location.host}/partner/${user?.affiliateCode}`;

  return (
    <div className="account-settings-component">
      <div className="information-block">
        <h3>My Information</h3>
        {user && (
          <>
            <Row align="middle" justify="center" gutter={[20, 20]}>
              <Col xs={24} md={4}>
                <div className="avatar-block">
                  <Upload
                    accept=".jpeg,.jpg,.png,.gif,.svg"
                    showUploadList={false}
                    beforeUpload={async (file) => {
                      await updateAvatar(file);
                      return false;
                    }}
                  >
                    <UserAvatar user={user} />
                  </Upload>
                  <Button
                    type="primary"
                    shape="circle"
                    icon={
                      <Tooltip title="Remove Profile Photo">
                        <DeleteOutlined />
                      </Tooltip>
                    }
                    className="remove-avatar"
                    onClick={() => updateAvatar()}
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={10}>
                <Row gutter={[0, 20]}>
                  <Col xs={24}>{renderInfoField('Name', getFullName(user))}</Col>
                  <Col xs={24}>{renderInfoField('Gender', getUserGender(user.gender))}</Col>
                </Row>
              </Col>
              <Col xs={24} sm={12} md={10}>
                <Row gutter={[0, 20]}>
                  <Col xs={24}>{renderInfoField('Email', user.email)}</Col>
                  <Col xs={24}>{renderInfoField('Password', '••••••••••••')}</Col>
                </Row>
              </Col>
            </Row>
            <Row align="top" justify="space-between" className="bottom-block">
              <Col sm={24} md={18}>
                <div>
                  {user.affiliateCode ? (
                    <>
                      <Space direction="vertical" size={6}>
                        <strong>Affiliate program</strong>
                        <Typography>Your code: {user.affiliateCode}</Typography>
                        <Typography>
                          Your link: <a href={affiliateLink}>{affiliateLink}</a>
                        </Typography>
                      </Space>
                    </>
                  ) : user.affiliateUser?.affiliateCode ? (
                    <Typography>
                      Partner:{' '}
                      <Button type="link" onClick={handleUpdatePartner} style={{ padding: 0, height: 'auto' }}>
                        {user.affiliateUser.affiliateCode}
                      </Button>
                    </Typography>
                  ) : null}
                </div>
              </Col>
              <Col sm={24} md={6} className="text-right">
                <Button onClick={handleEditProfile} className="edit-button">
                  Edit profile
                </Button>
              </Col>
            </Row>
          </>
        )}
      </div>

      {user?.tenantOrganizationId && (
        <div className="hipaa-authorization-block">
          <Row align="middle" justify="space-between" gutter={[20, 20]}>
            <Col>
              <Space size={16} align="start">
                <div className="hipaa-icon">
                  <SafetyCertificateOutlined />
                </div>
                <div className="hipaa-info">
                  <div className="hipaa-title">HIPAA Authorization</div>
                  <div className="hipaa-description">Manage your authorization status</div>
                </div>
              </Space>
            </Col>
            <Col>
              <Flex gap={12} align="center">
                <HipaaStatusTag status={user?.hipaaStatus} />
                <Button onClick={handleManageHipaa}>Manage</Button>
              </Flex>
            </Col>
          </Row>
        </div>
      )}

      <UpdatePartnerModalComponent />
      <EditAccountModalComponent />
    </div>
  );
}

export default AccountSettingsComponent;
